/**
 * Audit — the append-only record of what operators did (ADR-044).
 *
 * Every money action writes a row to `operator_audit_log`; this screen is its
 * first reader. Read-only by construction: the server exposes no write, the
 * table's trigger refuses UPDATE and DELETE, and the panel offers nothing but
 * filters. Rows expand to show the full recorded detail — the before/after,
 * the reason, the payout table on an edge change — because the detail is the
 * evidence, and evidence trimmed to fit a table row stops being evidence.
 */
import { useState, type ReactNode } from 'react';
import { fetchAudit } from '../api/client.ts';
import type { AuditEntry } from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute, age } from '../lib/datetime.ts';
import { Card, EmptyState, ErrorState, TableSkeleton } from '../ui/primitives.tsx';
import { IconPulse, IconSearch } from '../ui/icons.tsx';

const PAGE = 50;

const COLUMNS = ['text', 'text', 'text', 'text', 'text', 'num'] as const;

interface Filters {
  action: string;
  operatorId: string;
  from: string;
  to: string;
}

const EMPTY: Filters = { action: '', operatorId: '', from: '', to: '' };

export function Audit(): ReactNode {
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  /** Keyset cursor per loaded page; entries accumulate under "Load more". */
  const [older, setOlder] = useState<AuditEntry[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const head = useAsync(async () => {
    setOlder([]);
    const first = await fetchAudit(queryOf(applied));
    setExhausted(first.length < PAGE);
    return first;
  }, [applied]);

  const entries = [...(head.data ?? []), ...older];

  async function loadMore(): Promise<void> {
    const last = entries[entries.length - 1];
    if (!last) return;
    setLoadingMore(true);
    try {
      const next = await fetchAudit({ ...queryOf(applied), before: last.id });
      setOlder((prev) => [...prev, ...next]);
      if (next.length < PAGE) setExhausted(true);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="stack">
      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(draft);
        }}
      >
        <input
          className="input"
          value={draft.action}
          placeholder="Action prefix, e.g. game. or withdrawal.approve"
          onChange={(e) => setDraft({ ...draft, action: e.target.value })}
        />
        <input
          className="input"
          value={draft.operatorId}
          placeholder="Operator id"
          inputMode="numeric"
          style={{ maxWidth: '9rem' }}
          onChange={(e) => setDraft({ ...draft, operatorId: e.target.value.replace(/\D/g, '') })}
        />
        <input
          className="input"
          type="date"
          value={draft.from}
          title="From (UTC day, inclusive)"
          onChange={(e) => setDraft({ ...draft, from: e.target.value })}
        />
        <input
          className="input"
          type="date"
          value={draft.to}
          title="To (UTC day, inclusive)"
          onChange={(e) => setDraft({ ...draft, to: e.target.value })}
        />
        <button type="submit" className="btn btn--sm btn--primary">
          <IconSearch size="sm" />
          Filter
        </button>
        {applied !== EMPTY && (applied.action || applied.operatorId || applied.from || applied.to) ? (
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => {
              setDraft(EMPTY);
              setApplied(EMPTY);
            }}
          >
            Clear
          </button>
        ) : null}
      </form>

      <Card
        title="Operator audit log"
        sub="Append-only. Every money action lands here with its actor, reason and before/after"
        icon={<IconPulse />}
        count={entries.length}
        flush
      >
        {head.error && entries.length === 0 ? (
          <ErrorState title="The log did not load" detail={head.error} onRetry={head.reload} />
        ) : head.loading && entries.length === 0 ? (
          <TableSkeleton columns={COLUMNS} rows={10} />
        ) : entries.length === 0 ? (
          <EmptyState
            compact
            icon={<IconPulse size="lg" />}
            title="Nothing matches these filters"
            hint="The action filter is a prefix — 'game.' catches every game action."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>When</th>
                  <th>Operator</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th className="right">IP</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <AuditRow
                    key={e.id}
                    entry={e}
                    expanded={expanded === e.id}
                    onToggle={() => setExpanded(expanded === e.id ? null : e.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {entries.length > 0 && !exhausted ? (
        <div className="toolbar">
          <span className="card__spacer" />
          <button
            type="button"
            className="btn btn--sm"
            disabled={loadingMore}
            onClick={() => void loadMore()}
          >
            {loadingMore ? 'Loading…' : 'Load older entries'}
          </button>
          <span className="card__spacer" />
        </div>
      ) : null}
    </div>
  );
}

function AuditRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: AuditEntry;
  expanded: boolean;
  onToggle: () => void;
}): ReactNode {
  return (
    <>
      <tr className="row--clickable" onClick={onToggle} aria-expanded={expanded}>
        <td className="mono dim">{entry.id}</td>
        <td className="nowrap mono" title={absolute(entry.createdAt)}>
          {age(entry.createdAt)}
        </td>
        <td className="nowrap cell-lead">
          {entry.operatorUsername ?? <span className="dim">machine</span>}
        </td>
        <td className="mono nowrap">{entry.action}</td>
        <td className="mono dim nowrap">
          {entry.targetType ?? '—'}
          {entry.targetId !== null ? ` #${entry.targetId}` : ''}
        </td>
        <td className="right mono muted">{entry.ip ?? '—'}</td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={6}>
            <pre
              className="mono"
              style={{
                margin: 0,
                padding: '8px 4px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '12px',
                color: 'var(--text-secondary)',
              }}
            >
              {JSON.stringify(entry.detail ?? {}, null, 2)}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function queryOf(f: Filters): {
  action?: string;
  operatorId?: number;
  from?: string;
  to?: string;
  limit: number;
} {
  return {
    limit: PAGE,
    ...(f.action.trim() === '' ? {} : { action: f.action.trim() }),
    ...(f.operatorId === '' ? {} : { operatorId: Number(f.operatorId) }),
    ...(f.from === '' ? {} : { from: f.from }),
    ...(f.to === '' ? {} : { to: f.to }),
  };
}
